import * as React from 'react';
import { useIntl } from '@grandvisionhq/state';
import { useGetCart } from '../../hooks/use-get-cart.js';
import { useRecoverInactiveCart } from '../../hooks/use-recover-inactive-cart.js';
import { getValidationErrorsByLineItem } from '../../utils/get-validation-errors-by-line-item.js';
import { CartContext } from './context.js';
import { useCartSettings } from '../CartSettings/context.js';
import { CheckoutState } from '../../models/checkoutState/checkoutState.js';
import { lineItemDataMapper } from '../../utils/line-item-data-mapper.js';
import { getCartPrices } from '../../utils/get-cart-prices.js';
import { getDiscountDescriptions } from '../../utils/get-discount-descriptions.js';
import { getActiveVoucherCodes } from '../../utils/get-active-voucher-codes.js';
import { getSubscriptionDetails } from '../../utils/get-subscription-details.js';
import { getAddress } from '../../utils/get-address.js';
import { getShippingMethod } from '../../utils/get-shipping-method.js';
import { getCustomerDetails } from '../../utils/get-customer-details.js';
import { createContainsHelper } from '../../utils/create-contains-helper.js';

const CartProvider = ({ children  })=>{
    var ref3, ref1;
    const { called , refetch , loading , data , error , fetchByCartId , reset  } = useGetCart();
    const onRecoveryComplete = React.useCallback((cartId, cartHash)=>{
        fetchByCartId(cartId, cartHash);
    }, [
        fetchByCartId
    ]);
    const [recoverCart, { called: isRecoveryAttempted  }] = useRecoverInactiveCart({
        reset,
        onComplete: onRecoveryComplete
    });
    const settings = useCartSettings();
    const intl = useIntl();
    const cart1 = React.useMemo(()=>{
        var ref, ref2;
        if (data === null) {
            return null;
        }
        if (!data) {
            return undefined;
        }
        const cart = data === null || data === void 0 ? void 0 : (ref = data.getCart) === null || ref === void 0 ? void 0 : ref.cart;
        if (cart.checkoutState === CheckoutState.Finalized && !isRecoveryAttempted) {
            recoverCart(cart.id);
        }
        const lineItemsRaw = cart.lineItems;
        const lineItems = lineItemDataMapper(lineItemsRaw, settings, intl);
        return {
            id: cart.id,
            numberOfItems: cart.numberOfItems,
            lineItems,
            validationErrors: getValidationErrorsByLineItem(data === null || data === void 0 ? void 0 : (ref2 = data.getCart) === null || ref2 === void 0 ? void 0 : ref2.validationErrors),
            prices: getCartPrices(cart.price),
            discountDescriptions: getDiscountDescriptions(cart.appliedDiscounts, intl.locale),
            voucherCodes: getActiveVoucherCodes(cart.vouchers),
            subscription: getSubscriptionDetails(cart),
            isAccountRequired: !!cart.isAccountRequired,
            billingAddress: getAddress(cart.billingAddress),
            shippingAddress: getAddress(cart.shippingAddress),
            shippingMethod: getShippingMethod(cart.shippingMethod),
            customerDetails: getCustomerDetails(cart.customerDetails),
            checkoutState: cart.checkoutState,
            contains: createContainsHelper(lineItems)
        };
    // we need data?.getCart.cart as a dependency, data doesn't change on cart updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        data,
        data === null || data === void 0 ? void 0 : (ref3 = data.getCart) === null || ref3 === void 0 ? void 0 : ref3.cart,
        data === null || data === void 0 ? void 0 : (ref1 = data.getCart) === null || ref1 === void 0 ? void 0 : ref1.validationErrors,
        intl,
        settings,
        isRecoveryAttempted, 
    ]);
    const value = React.useMemo(()=>({
            cart: cart1,
            refetch,
            loading,
            error,
            called,
            fetchByCartId
        })
    , [
        cart1,
        refetch,
        called,
        loading,
        error,
        fetchByCartId
    ]);
    if (typeof children === 'function') {
        return /*#__PURE__*/ React.createElement(CartContext.Provider, {
            value: value
        }, children(value));
    }
    return /*#__PURE__*/ React.createElement(CartContext.Provider, {
        value: value
    }, children);
};

export { CartProvider };
//# sourceMappingURL=provider.js.map
